import { createFileRoute } from '@tanstack/react-router'
import type {
  InvestorFirmData,
  InvestorFocusData,
  InvestorPersonData,
} from '@workspace/seedforge-ui/investor-profile'
import { InvestorProfile } from '@workspace/seedforge-ui/investor-profile'
import { INVESTOR_NAV_LABEL } from '@workspace/seedforge-ui/lib/investor-nav'
import type { SettingsInvite, SettingsUser } from '@workspace/seedforge-ui/settings'
import { SettingsView } from '@workspace/seedforge-ui/settings'
import { useCallback } from 'react'
import { AssistantSwitch } from '../../../../components/assistant-switch'
import { InvestorLayout } from '../../../../components/investor-layout'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import {
  NotificationsSection,
  PrivacySection,
} from '../../../../features/investor-assistant/components/settings-sections'
import {
  usePersonalFocusQuery,
  useSavePersonalFocusMutation,
} from '../../../../features/investor-assistant/hooks/use-investor-assistant'
import { InvestorSettingsPage as ClassicSettingsPage } from '../../../../features/legacy-investor/settings-page'
import {
  uploadDocument,
  useCancelInvestorInviteMutation,
  useCreateDocumentMutation,
  useCreateInvestorInviteMutation,
  useInvestorConfigQuery,
  useInvestorInvitesQuery,
  useInvestorPersonaQuery,
  useUpdateInvestorMemberMutation,
  useUpdateInvestorMutation,
  useUpdateUserMutation,
  useVerifyDocumentMutation,
} from '../../../../hooks/api'

export const Route = createFileRoute('/app/investor/$id/settings')({
  component: () => (
    <AssistantSwitch assistant={<InvestorSettingsPage />} classic={<ClassicSettingsPage />} />
  ),
  validateSearch: (search: Record<string, unknown>): { focus?: true } =>
    search.focus === true || search.focus === 'true' ? { focus: true } : {},
})

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

function InvestorSettingsPage() {
  const { user, investor } = useInvestorContext()
  // The watchdog's Edit focus lands here with ?focus=true: the focus card opens for editing.
  const { focus } = Route.useSearch()
  const investorId = investor.id
  const { data: invites } = useInvestorInvitesQuery(investorId)
  const createInvite = useCreateInvestorInviteMutation(investorId)
  const cancelInvite = useCancelInvestorInviteMutation(investorId)
  const { data: investorConfig } = useInvestorConfigQuery()
  const { data: persona } = useInvestorPersonaQuery(investorId)
  const updateInvestor = useUpdateInvestorMutation(investorId)
  // The focus is the person's own (handoff ruling 13), the same focus the watchdog shows; the firm card is the firm's.
  const { data: personalFocus } = usePersonalFocusQuery()
  const saveFocus = useSavePersonalFocusMutation()
  const updateMember = useUpdateInvestorMemberMutation(investorId)
  const updateUser = useUpdateUserMutation()
  const createDoc = useCreateDocumentMutation()
  const verifyDoc = useVerifyDocumentMutation()

  const handlePersonSave = (data: InvestorPersonData) => {
    updateUser.mutate({ body: { name: data.name } })
    updateMember.mutate({ path: { investorId }, body: { title: data.title || null } })
  }

  const handleFirmSave = (data: InvestorFirmData) => {
    updateInvestor.mutate({
      path: { investorId },
      body: {
        name: data.fundName || undefined,
        description: data.fundDescription || null,
        websiteUrl: data.websiteUrl || null,
      },
    })
  }

  const handleFocusSave = (data: InvestorFocusData) => {
    saveFocus.mutate({ body: { ...data, killCriteria: data.killCriteria || null } })
  }

  const handleThesisRemove = () => {
    updateInvestor.mutate({ path: { investorId }, body: { thesisDocumentId: null } })
  }

  const handleThesisUpload = useCallback(
    async (file: File) => {
      const { documentId } = await uploadDocument(file, createDoc, verifyDoc)
      await updateInvestor.mutateAsync({
        path: { investorId },
        body: { thesisDocumentId: documentId },
      })
    },
    [createDoc, verifyDoc, updateInvestor, investorId]
  )

  const isOwner = investor.role === 'OWNER'

  const users: SettingsUser[] = [
    {
      id: user.id,
      name: user.name ?? user.email ?? 'Unknown',
      email: user.email ?? '',
      initials: getInitials(user.name ?? null, user.email ?? null),
      role: investor.role ?? 'READ',
      userId: user.id,
    },
  ]

  const pendingInvites: SettingsInvite[] = (invites ?? []).map((i) => ({
    id: i.id,
    email: i.email,
    role: i.role,
    token: i.token,
    createdAt: i.createdAt,
  }))

  return (
    <InvestorLayout activeNav={INVESTOR_NAV_LABEL.settings}>
      <div className="mx-auto max-w-3xl space-y-6 py-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your profile, team, notifications and privacy
          </p>
        </div>
        <InvestorProfile
          personName={user.name ?? user.email ?? 'Investor'}
          personTitle={investor.memberTitle}
          persona={persona}
          fundName={investor.name}
          fundDescription={investor.description ?? undefined}
          websiteUrl={investor.websiteUrl}
          investmentAreas={personalFocus?.investmentAreas}
          investmentGeographies={personalFocus?.investmentGeographies}
          investmentStages={personalFocus?.investmentStages}
          minTicketSize={personalFocus?.minTicketSize}
          maxTicketSize={personalFocus?.maxTicketSize}
          ticketCurrency={personalFocus?.ticketCurrency}
          focusKeywords={personalFocus?.focusKeywords}
          killCriteria={personalFocus?.killCriteria}
          areaOptions={investorConfig?.investmentAreas}
          regionOptions={investorConfig?.investmentRegions}
          thesisFileName={investor.thesisDocument?.fileName}
          editable={isOwner}
          focusEditable={personalFocus !== undefined}
          onPersonSave={handlePersonSave}
          onFirmSave={handleFirmSave}
          onFocusSave={handleFocusSave}
          editFocus={focus === true}
          onThesisUpload={handleThesisUpload}
          onThesisRemove={handleThesisRemove}
        />
        <SettingsView
          hideHeading
          users={users}
          invites={pendingInvites}
          onInvite={(email, role) =>
            createInvite.mutate({
              body: { email, role },
              path: { investorId },
            })
          }
          onCancelInvite={(inviteId) =>
            cancelInvite.mutate({
              path: { investorId, inviteId },
            })
          }
          isInviting={createInvite.isPending}
          isOwner={isOwner}
          widthClassName="max-w-3xl"
        />
        <NotificationsSection />
        <PrivacySection />
      </div>
    </InvestorLayout>
  )
}
